// Copyright (c) Hamilton Manufacturing Corp.
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { lastKeyPress, mobileView, pascalCase } from './api/utility';
import Button from './form/Button';
import Icon from './form/Icon';
import { useGlobalState } from './globalState/GlobalStateProvider';

const growIn = keyframes`
    0% { transform: scale(0.1); }
    70% { transform: scale(1.1); }
    100% { transform: scale(1); }
`
const shrinkOut = keyframes`
    0% { transform: scale(1); }
    30% { transform: scale(1.1); }
    100% { transform: scale(0); }
`

const fallDown = keyframes`
    0% { transform: translateY(-101%); }
    70% { transform: translateY(3%); }
    100% { transform: translateY(0%); }
`;

const riseUp = keyframes`
    0% { transform: translateY(0%); }
    30% { transform: translateY(3%); }
    100% { transform: translateY(-101%); }
`;
const slideIn = keyframes`
    0% { transform: translateX(101%); }
    70% { transform: translateX(-3%); }
    100% { transform: translateX(0%); }
`;

const slideOut = keyframes`
    0% { transform: translateX(0%); }
    30% { transform: translateX(3%); }
    100% { transform: translateX(-101%); }
`;

const Frame = styled.div`
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    justify-content: center;
    align-items: center;
    background: #6c6c6c87;
    transition: opacity 0.15s linear;
    position: fixed;
    display: flex;

    animation: ${({ showing }) => showing ? 'fadeIn' : 'fadeOut'};
    animation-duration: 0.3s;
    animation-fill-mode: forwards;
`
const Body = styled.div`
    background: ${({ theme }) => theme.modal.body};
    border-radius: 0.5rem;
    position: relative;
    animation: growin 0.5s;
    overflow: hidden;
    transition: all 0.15s ease-in-out;
    max-width: ${({ mobile }) => mobile ? '97%' : '50%'};
    min-width: ${({ mobile }) => mobile ? '85vw' : '25%'};
    box-shadow: 5px 5px 20px 0 #00000082;

    animation: ${({ mobile, showing, followUp }) => {
        if (mobile) {
            // return showing ? (followUp ? slideOut : riseUp) : (followUp ? slideIn : riseDown);
            if (showing) {
                return followUp ? slideIn : fallDown
            } else {
                return followUp ? slideOut : riseUp
            }
        } else {
            return showing ? growIn : shrinkOut;
        }
    }};
    animation-duration: 0.3s;
    animation-fill-mode: forwards;
`
const Margin = styled.div`
    height: 100%;
    width: 10px;
    position: absolute;
    background: ${({ theme, type }) => theme.modal[type] || theme.modal.alert};
`
const Wrapper = styled.div`
    padding: ${({ mobile }) => mobile ? '0 1em' : '0 2.5rem'};
`
const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`
const HeaderActions = styled.div`
    display: flex;
    align-items: center;
`
const CloseX = styled.button`
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.mobile.text};
    opacity: 0.7;
    cursor: pointer;
    font-size: 1.2em;
    padding: 0.25em;
    margin-right: -0.25em;

    &:hover {
        opacity: 1;
    }
`
const Title = styled.div`
    padding: 1.5rem 0;
    font-size: 1.5em;
    font-weight: 500;
    line-height: 1.2;
    display: flex;
    align-items: center;
`
const I = styled(Icon)`
    margin: 0 0.5em 0 0.1em;
    color: ${({ theme, type }) => theme.modal[type] || theme.modal.alert};
    ${({ type }) => type === 'promise' ? 'animation: 2s rotate360 infinite linear;' : ''}
`
const Text = styled.div`
    max-height: 60vh;
    overflow: overlay;
`
const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
`

const Modal = ({ ...props }) => {
    const { state: { }, } = useGlobalState();

    const defaultModal = {
        show: false,
        title: 'Test',
        type: 'alert',
        body: 'Why can you see this?',
        buttons: [],
        icon: '',
        hideClose: false,
        closeAsX: false,
        closeText: '',
        closeColor:''
    }

    const [showing, setShowing] = useState(false);
    const [followUp, setFollowUp] = useState(false);
    const [modal, setModal] = useState(defaultModal)

    const promiseModal = c => {
        return new Promise((resolve, reject) => {
            setShowing(true)
            c.type = 'promise';
            c.show = true;
            showModal(c);
            if (c.callback)
                c.callback
                    .then(res => {
                        resolve(res)
                        if (c.closeAfter) handleClose();
                    })
                    .catch(ex => {
                        showModal({
                            title: 'Error',
                            body: ex.blob || 'Sorry, something went wrong.',
                            type: 'error'
                        })
                        reject(ex)
                    })
        })
            .catch((e) => {
                if (e !== false) console.trace(e);
                const errorMessage = {
                    error: 'Promise Rejected',
                    blob: e.blob
                };
                throw errorMessage
            });
    }

    const confirmationModal = c => {
        return new Promise((resolve, reject) => {
            setShowing(true)
            c.buttons = c.buttons || [];
            c.show = true;
            c.hideClose = true;
            c.buttons.push(
                <Button key="confirm" onClick={() => resolve(true)} autoFocus>Yes</Button>,
                <Button key="deny" onClick={() => resolve(false)}>No</Button>
            );
            showModal(c);
        })
            .catch((e) => {
                if (e !== false) console.trace(e);
                const errorMessage = {
                    error: 'Confirmation Rejected'
                };
                showModal({
                    title: 'Error',
                    body: e.blob || 'Sorry, something went wrong.',
                    type: 'error'
                })
                throw errorMessage
            })
            .finally(() => {
                if (c.closeAfter) handleClose();
            });
    }

    const iconType = (type) => {
        switch (type) {
            case 'success':
                return 'checkCircle';
            case 'warning':
                return 'exclamationCircle';
            case 'error':
                return 'exclamationTriangle';
            case 'promise':
                return 'hourglass';
            default:
                return 'infoCircle';
        }
    }

    const handleClose = (follow) => {
        return new Promise((resolve) => {
            setShowing(false);
            if (!follow) setFollowUp(false)
            setTimeout(() => {
                if (modal.promise) {
                    modal.promise(true)
                }
                setModal(defaultModal)
                setTimeout(() => {
                    resolve(true)
                }, 50);
            }, 300);
        });
    }

    const showModal = async config => {
        if (modal.show) {
            setFollowUp(true)
            await handleClose(true);
        }
        setShowing(true)
        lastKeyPress.registerListener(key => {
            if (key === 'Escape') handleClose();
        });

        return new Promise((resolve) => {
            setModal({
                ...config,
                show: true,
                promise: resolve
            })
        })
    }

    const updateBody = body => {
        setModal({
            ...modal,
            body: body,
        })
    }

    Modal.Promise = promiseModal;
    Modal.Confirmation = confirmationModal;
    Modal.UpdateBody = updateBody;
    Modal.Show = showModal;
    Modal.Close = handleClose;

    return modal.show && (
        <Frame showing={showing}>
            <Body mobile={mobileView} showing={showing} followUp={followUp}>
                <Margin type={modal.type} />
                <Wrapper mobile={mobileView}>
                    <Header>
                        <Title>
                            <I type={modal.type} icon={modal.icon || iconType(modal.type)} />
                            <span autoFocus>{pascalCase(modal.title)}</span>
                        </Title>
                        {(modal.type !== 'promise' && !modal.hideClose && modal.closeAsX) && (
                            <HeaderActions>
                                <CloseX
                                    type='button'
                                    onClick={() => handleClose()}
                                    aria-label={modal.closeText || 'Close'}
                                    title={modal.closeText || 'Close'}
                                >
                                    <Icon icon='times' />
                                </CloseX>
                            </HeaderActions>
                        )}
                    </Header>
                    <div>
                        <Text>
                            {modal.body}
                        </Text>
                        <Footer>
                            {modal.buttons}
                            {(modal.type !== 'promise' && !modal.hideClose && !modal.closeAsX) && <Button style={{ backgroundColor: (modal.closeColor ? modal.closeColor : null) }} autoFocus onClick={() => handleClose()}>{modal.closeText ? modal.closeText : 'Close'}</Button>}
                        </Footer>
                    </div>
                </Wrapper>
            </Body>
        </Frame>
    );
}

export default Modal;

const handlePromise = () => {
    Modal.Promise({
        show: true,
        title: 'Promise Modal Test',
        body: 'Testing modal promise.',
        callback: testPromise()
    })
        .then(() => Modal.SetModal({
            show: true,
            title: 'Success',
            body: 'Testing Successful',
        }))
}

const handleConfirmation = () => {
    Modal.Confirmation({
        show: true,
        title: 'Confirmation Modal Test',
        body: 'Testing modal confirmation.',
        callback: testPromise()
    })
        .then((res) => res && handlePromise())
}

const testPromise = () => new Promise(res => setTimeout(() => res(1), randomIntBetween(1000, 2000)))
const randomIntBetween = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);
